import { Component, OnDestroy } from "@angular/core";
import { Router } from "@angular/router";
import { ProductionStatus } from "@enums/productionStatus.enum";
import { AdminHeaderButton } from "@interfaces/adminHeaderButton.interface";
import { Machine } from "@interfaces/models/machine.interface";
import { Process } from "@interfaces/process.interface";
import { UserProfile } from "@interfaces/userProfile.interface";
import { AppSettingsService } from "@services/appSettings.service";
import { MachineService } from "@services/machine.service";
import { ProcessService } from "@services/process.service";
import { OperationService } from "@services/operation.service";
import { ProductionToolProgramService } from "@services/productionToolProgram.service";
import { UserProfileService } from "@services/userProfile.service";
import { ToastService } from "angular-toastify";
import { forkJoin } from "rxjs";
import { ToastApiError } from "@helpers/apiError";
import { CheckGroupPermissions } from "@helpers/checkGroupPermissions";
import { ToolroomCutterLabel, ToolroomMachineStatus, ToolroomMinutes } from "@helpers/toolroomMachineStatus";
import { ToolroomJobProgress } from "@helpers/toolroomProgress";

@Component({
	moduleId: module.id,
	selector: "toolOrderPlanning",
	styleUrls: ["toolOrderPlanning.css"],
	templateUrl: "toolOrderPlanning.html"
})
export class ToolOrderPlanningAdminComponent implements OnDestroy {
	allStages: any[] = [];
	// Mirrors the roles on MachineController.UpdateProductionStatusSupervisor. Without it the control is hidden
	// rather than left to fail on the endpoint.
	canChangeMachineStatus = false;
	currentView = "machine";
	dragOverGroupId: any = null;
	dragOverStageId: number | null = null;
	draggedFromGroupId: any = null;
	draggedStageId: number | null = null;
	grouped: any[] = [];
	headerPrimaryButtons: AdminHeaderButton[] = [];
	headerTertiaryButtons: AdminHeaderButton[] = [];
	isLoading = false;
	// The statuses the planner offers, worded and ordered as the operator terminal and the wall board read them
	// rather than as the raw enum names, so the same machine says the same thing on both screens.
	machineStatuses = [
		ProductionStatus.Active,
		ProductionStatus.Changeover,
		ProductionStatus.QualityCheck,
		ProductionStatus.QLoop,
		ProductionStatus.Idle,
		ProductionStatus.Break,
		ProductionStatus.ToiletBreak,
		ProductionStatus.ToolTipChange,
		ProductionStatus.SwarfCleanOut,
		ProductionStatus.Maintenance,
		ProductionStatus.Breakdown,
		ProductionStatus.InitialCheckFailed,
		ProductionStatus.AssistanceRequired,
		ProductionStatus.Inactive
	].map((status) => ({ id: status, name: ToolroomMachineStatus(status).label }));
	machines: Machine[] = [];
	operators: UserProfile[] = [];
	opsPlanningDate: string | number | Date = new Date();

	get pausedCount(): number {
		return this.allStages.filter((s) => s.stageStarted && !s.stageComplete && s.isPaused).length;
	}

	popupCloseButtonOptions: any = { onClick: () => { this.popupVisible = false; }, text: "Close" };
	popupEstimatedRunTime = 0;
	popupEstimatedSetupTime = 0;
	popupEstimatedStartDate: string | number | Date = new Date();
	popupMachineId: number | null = null;
	popupMachineStatus: ProductionStatus | null = null;
	popupOperatorId: number | null = null;
	popupSaveButtonOptions: any;
	popupStageId = 0;
	popupStatusMachineId = 0;
	popupTitle = "";
	popupVisible = false;
	processes: Process[] = [];

	get queuedCount(): number {
		return this.allStages.filter((s) => !s.stageStarted && !s.stageComplete).length;
	}

	get runningCount(): number {
		return this.allStages.filter((s) => s.stageStarted && !s.stageComplete && !s.isPaused).length;
	}

	// Estimated progress of each running operation (time left, which cutter should be in), keyed by operation id.
	progressByOperation: { [operationId: number]: any } = {};
	private progressTimer: any;
	title = "Billet Planner";

	get unassignedCount(): number {
		return this.allStages.filter((s) => !s.stageComplete && !s.machineId).length;
	}

	useDate = true;

	constructor(private router: Router, private toastService: ToastService, private machineService: MachineService, private processService: ProcessService, private stageService: OperationService, private userProfileService: UserProfileService, private programService: ProductionToolProgramService, appSettingsService: AppSettingsService) {
		this.canChangeMachineStatus = CheckGroupPermissions(appSettingsService, "Super Admin") || CheckGroupPermissions(appSettingsService, "Supervisor");
		this.buildButtons();
		this.getMachines();
		this.getOperators();
		this.getProcesses();
	}

	autoPlan() {
		if (confirm("Auto-plan all blocks by scheduling their operations backwards from the due dates? This overwrites current estimated dates and machine assignments.") === false) return;
		this.isLoading = true;
		this.stageService.autoPlan()
			.subscribe(
				(res: any) => {
					const r = res.response;
					const msg = `Planned ${r.plannedOperations} op(s) across ${r.plannedBlocks} block(s) (${r.bufferDays}-day buffer).`;
					if (r.atRiskCount > 0) this.toastService.warn(`${msg} ${r.atRiskCount} block(s) at risk of missing the due date.`);
					else this.toastService.success(`${msg} All on track.`);
					this.getData();
				},
				(err) => { console.log(err); this.isLoading = false; ToastApiError(this.toastService, err, "Auto-plan failed."); }
			);
	}

	buildButtons() {
		this.headerPrimaryButtons = [{ icon: "fal fa-wand-magic-sparkles", method: "autoPlan", text: "Auto-plan" }];
		this.headerTertiaryButtons = [];
	}

	buildGrouped() {
		const groups: { [key: string]: any } = {};

		// Pre-populate with all machines/operators so empty ones show too
		if (this.currentView === "machine") {
			for (const m of this.machines) {
				// The machine and its status are read off the lane rather than looked up in the template, the way the
				// toolroom overview decorates its cards. A lane built from a stage below - an operation sitting on a
				// machine at another site - has neither, and shows no status control.
				groups[m.id] = { availableStages: [], groupId: m.id, groupName: (m as any).cellNumber || "Unknown", machine: m, runningStages: [], status: ToolroomMachineStatus(m.productionStatus) };
			}
			groups[0] = { availableStages: [], groupId: 0, groupName: "Unassigned", runningStages: [] };
		} else if (this.currentView === "operator") {
			for (const op of this.operators) {
				groups[op.id] = { availableStages: [], groupId: op.id, groupName: `${(op as any).firstName} ${(op as any).lastName}`, runningStages: [] };
			}
			groups[0] = { availableStages: [], groupId: 0, groupName: "Unassigned", runningStages: [] };
		}

		for (const stage of this.allStages) {
			let groupId: any;
			let groupName: string;

			switch (this.currentView) {
				case "machine":
					groupId = stage.machineId || 0;
					groupName = stage.machine ? stage.machine.cellNumber : "Unassigned";
					break;
				case "productionTool":
					groupId = stage.toolOrder ? stage.toolOrder.productionToolId : 0;
					groupName = stage.toolOrder && stage.toolOrder.productionTool ? stage.toolOrder.productionTool.name : "Unknown";
					break;
				case "operator":
					groupId = stage.assignedUserProfileId || 0;
					groupName = stage.assignedUserProfile ? `${stage.assignedUserProfile.firstName} ${stage.assignedUserProfile.lastName}` : "Unassigned";
					break;
				default:
					groupId = 0;
					groupName = "Unknown";
			}

			if (!groups[groupId]) {
				groups[groupId] = { availableStages: [], groupId, groupName, runningStages: [] };
			}

			if (stage.stageStarted && !stage.stageComplete) {
				groups[groupId].runningStages.push(stage);
			} else {
				groups[groupId].availableStages.push(stage);
			}
		}

		this.grouped = Object.values(groups).sort((a, b) => a.groupName.localeCompare(b.groupName));
	}

	changeDate(e: any) {
		this.opsPlanningDate = e.value;
		this.getData();
	}

	changeEstimatesPopup(stageId: number, stage: any) {
		this.popupStageId = stageId;
		this.popupEstimatedStartDate = stage.estimatedStartDate || new Date();
		this.popupEstimatedSetupTime = stage.estimatedSetupTime;
		this.popupEstimatedRunTime = stage.estimatedRunTime;
		this.popupTitle = "Change Estimates";
		this.popupSaveButtonOptions = { onClick: () => this.saveEstimates(), text: "Save" };
		this.popupVisible = true;
	}

	changeMachinePopup(stageId: number, currentMachineId: number | null) {
		this.popupStageId = stageId;
		this.popupMachineId = currentMachineId;
		this.popupTitle = "Change Machine";
		this.popupSaveButtonOptions = { onClick: () => this.saveMachine(), text: "Save" };
		this.popupVisible = true;
	}

	changeMachineStatusPopup(machine: any) {
		if (this.canChangeMachineStatus === false || !machine) return;
		this.popupStatusMachineId = machine.id;
		this.popupMachineStatus = machine.productionStatus;
		this.popupTitle = "Change Production Status";
		this.popupSaveButtonOptions = { onClick: () => this.saveMachineStatus(), text: "Save" };
		this.popupVisible = true;
	}

	changeOperatorPopup(stageId: number, currentOperatorId: number | null) {
		this.popupStageId = stageId;
		this.popupOperatorId = currentOperatorId;
		this.popupTitle = "Change Operator";
		this.popupSaveButtonOptions = { onClick: () => this.saveOperator(), text: "Save" };
		this.popupVisible = true;
	}

	changeView(view: string) {
		this.currentView = view;
		this.buildGrouped();
	}

	completeStage(stageId: number) {
		this.stageService.completeStage(stageId)
			.subscribe(
				() => { this.toastService.success("Stage completed"); this.getData(); },
				(err) => { console.log(err); ToastApiError(this.toastService, err); }
			);
	}

	getData() {
		this.loadProgress();
		this.isLoading = true;
		let request;

		if (this.useDate) {
			const date = new Date(this.opsPlanningDate);
			switch (this.currentView) {
				case "machine":
					request = this.stageService.plannerByMachineForDate(date); break;
				case "productionTool":
					request = this.stageService.plannerByProductionToolForDate(date); break;
				case "operator":
					request = this.stageService.plannerByOperatorForDate(date); break;
				default:
					request = this.stageService.plannerByMachineForDate(date);
			}
		} else {
			switch (this.currentView) {
				case "machine":
					request = this.stageService.plannerByMachine(); break;
				case "productionTool":
					request = this.stageService.plannerByProductionTool(); break;
				case "operator":
					request = this.stageService.plannerByOperator(); break;
				default:
					request = this.stageService.plannerByMachine();
			}
		}

		request.subscribe(
			(res: any) => {
				this.allStages = res.response;
				this.buildGrouped();
				this.isLoading = false;
			},
			(err) => {
				console.log(err);
				ToastApiError(this.toastService, err);
				this.isLoading = false;
			}
		);
	}

	getMachines() {
		this.machineService.getBySite(5)
			.subscribe(
				(res: any) => { this.machines = res.response; this.getData(); },
				(err) => console.log(err)
			);
	}

	getOperators() {
		this.userProfileService.bySite(5)
			.subscribe(
				(res: any) => { this.operators = res.response; },
				(err) => console.log(err)
			);
	}

	getProcesses() {
		this.processService.getAll()
			.subscribe(
				(res: any) => { this.processes = res.response; },
				(err) => console.log(err)
			);
	}

	goToToolOrder(toolOrderId: number) {
		this.router.navigate(["/admin/toolOrders/" + toolOrderId]);
	}

	headerButtonClick(method: any) {
		// @ts-ignore
		if (this[method]) this[method]();
	}

	// Drag and drop
	initials(profile: any): string {
		return profile ? `${(profile.firstName || "").charAt(0)}${(profile.lastName || "").charAt(0)}`.toUpperCase() : "";
	}

	// The toolroom overview carries each machine's running job with its program's cutters, which gives the same
	// estimate the operator terminal shows.
	loadProgress() {
		if (!this.progressTimer) {
			this.progressTimer = setInterval(() => this.loadProgress(), 60000);
		}
		this.machineService.toolroomOverview(0)
			.subscribe(
				(res: any) => {
					const byOperation: { [operationId: number]: any } = {};
					const now = new Date();
					(res.response || []).forEach((machine: any) => {
						const job = machine.current;
						const progress = job ? ToolroomJobProgress(job.dateStarted, job.cutters, job.estimatedRunTime, now) : null;
						if (!job || !progress) return;
						const cutter = progress.currentIndex >= 0 ? job.cutters[progress.currentIndex] : null;
						byOperation[job.id] = {
							currentIndex: progress.currentIndex,
							cutterLabel: ToolroomCutterLabel(cutter),
							cutterPosition: cutter ? `${progress.currentIndex + 1}/${job.cutters.length}` : "",
							finishAt: progress.finishAt,
							left: progress.overrun ? `+${ToolroomMinutes(progress.elapsedMinutes - progress.totalMinutes)}` : ToolroomMinutes(progress.remainingMinutes) || "0m",
							overrun: progress.overrun,
							percent: progress.percent,
							segments: progress.segments,
						};
					});
					this.progressByOperation = byOperation;
				},
				(err) => console.log(err)
			);
	}

	ngOnDestroy() {
		clearInterval(this.progressTimer);
	}

	onDragEnd() {
		this.draggedStageId = null;
		this.draggedFromGroupId = null;
		this.dragOverGroupId = null;
		this.dragOverStageId = null;
	}

	onDragLeaveGroup(groupId: any) {
		if (this.dragOverGroupId === groupId) this.dragOverGroupId = null;
	}

	onDragOverGroup(event: DragEvent, groupId: any) {
		event.preventDefault();
		if (event.dataTransfer) event.dataTransfer.dropEffect = "move";
		this.dragOverGroupId = groupId;
	}

	onDragOverStage(event: DragEvent, stageId: number) {
		event.preventDefault();
		this.dragOverStageId = stageId;
	}

	onDragStart(event: DragEvent, stageId: number, groupId: any) {
		this.draggedStageId = stageId;
		this.draggedFromGroupId = groupId;
		if (event.dataTransfer) event.dataTransfer.effectAllowed = "move";
	}

	onDropOnGroup(groupId: any) {
		if (!this.draggedStageId) return;
		if (this.draggedFromGroupId === groupId) return;

		if (this.currentView === "machine") {
			this.stageService.changeMachine({ machineId: groupId === 0 ? null : groupId, operationId: this.draggedStageId })
				.subscribe(() => this.getData(), (err) => { console.log(err); ToastApiError(this.toastService, err); });
		} else if (this.currentView === "operator") {
			this.stageService.changeOperator({ assignedUserProfileId: groupId === 0 ? null : groupId, operationId: this.draggedStageId })
				.subscribe(() => this.getData(), (err) => { console.log(err); ToastApiError(this.toastService, err); });
		}

		this.onDragEnd();
	}

	onDropOnStage(targetStageId: number, groupId: any) {
		if (!this.draggedStageId || this.draggedStageId === targetStageId) return;

		if (this.draggedFromGroupId !== groupId) {
			this.onDropOnGroup(groupId);
			return;
		}

		const group = this.grouped.find((g: any) => g.groupId === groupId);
		if (!group) return;

		const stages = [...group.availableStages].sort((a: any, b: any) => a.priority - b.priority);
		const draggedIndex = stages.findIndex((s: any) => s.id === this.draggedStageId);
		const targetIndex = stages.findIndex((s: any) => s.id === targetStageId);

		if (draggedIndex === -1 || targetIndex === -1) return;

		const [moved] = stages.splice(draggedIndex, 1);
		stages.splice(targetIndex, 0, moved);

		const updates = stages
			.map((stage: any, index: number) => ({ newPriority: index + 1, stage }))
			.filter(({ newPriority, stage }) => stage.priority !== newPriority)
			.map(({ newPriority, stage }) =>
				this.stageService.changePriority({ operationId: stage.id, priority: newPriority })
			);

		if (updates.length > 0) {
			forkJoin(updates).subscribe(() => this.getData(), (err) => { console.log(err); ToastApiError(this.toastService, err); });
		}

		this.onDragEnd();
	}

	// Each process keeps the same colour everywhere on the board, so a column of the same work reads at a glance.
	// The operation's program sheet, opened ready to print or save as a PDF.
	openProgram(stage: any) {
		this.programService.forOperation(stage.id)
			.subscribe(
				(res: any) => {
					const program = (res.response || [])[0];

					if (!program) {
						this.toastService.warn(`OP${(stage.stageNumber || 0) * 10} on ${stage.toolOrder?.name || "this billet"} has no program yet`);
						return;
					}
					window.open(`/admin/productionToolPrograms/${program.id}/print`, "_blank");
				},
				(err) => { console.log(err); ToastApiError(this.toastService, err, "Could not find the program"); }
			);
	}

	processColour(process: string): { background: string; color: string } {
		const palette = [
			{ background: "#e0e7ff", color: "#3730a3" },
			{ background: "#dcfce7", color: "#166534" },
			{ background: "#fef3c7", color: "#92400e" },
			{ background: "#fce7f3", color: "#9d174d" },
			{ background: "#e0f2fe", color: "#075985" },
			{ background: "#ede9fe", color: "#5b21b6" },
			{ background: "#ffedd5", color: "#9a3412" },
			{ background: "#ccfbf1", color: "#115e59" },
		];
		const key = (process || "").toUpperCase();
		let hash = 0;
		for (let i = 0; i < key.length; i++) {
			hash = (hash * 31 + key.charCodeAt(i)) >>> 0;
		}
		return palette[hash % palette.length];
	}

	saveEstimates() {
		const date = this.popupEstimatedStartDate ? new Date(this.popupEstimatedStartDate).toDateString() : null;
		this.stageService.changeEstimates({
			estimatedRunTime: this.popupEstimatedRunTime,
			estimatedSetupTime: this.popupEstimatedSetupTime,
			estimatedStartDate: date,
			operationId: this.popupStageId
		}).subscribe(
			() => { this.popupVisible = false; this.toastService.success("Estimates updated"); this.getData(); },
			(err) => { console.log(err); ToastApiError(this.toastService, err); }
		);
	}

	saveMachine() {
		this.stageService.changeMachine({ machineId: this.popupMachineId, operationId: this.popupStageId })
			.subscribe(
				() => { this.popupVisible = false; this.toastService.success("Machine updated"); this.getData(); },
				(err) => { console.log(err); ToastApiError(this.toastService, err); }
			);
	}

	// Through the supervisor endpoint rather than a plain machine update: it writes the production status change
	// audit and raises the machine event that Breakdown downtime is built from, both of which a patch would lose.
	saveMachineStatus() {
		if (this.popupMachineStatus === null) return;
		this.machineService.updateProductionStatusSupervisor(this.popupStatusMachineId, this.popupMachineStatus)
			.subscribe(
				() => { this.popupVisible = false; this.toastService.success("Production status updated"); this.getMachines(); },
				(err) => { console.log(err); ToastApiError(this.toastService, err, "Could not change the production status."); }
			);
	}

	saveOperator() {
		this.stageService.changeOperator({ assignedUserProfileId: this.popupOperatorId, operationId: this.popupStageId })
			.subscribe(
				() => { this.popupVisible = false; this.toastService.success("Operator updated"); this.getData(); },
				(err) => { console.log(err); ToastApiError(this.toastService, err); }
			);
	}

	startStage(stageId: number) {
		this.stageService.startStage(stageId)
			.subscribe(
				() => { this.toastService.success("Stage started"); this.getData(); },
				(err) => { console.log(err); ToastApiError(this.toastService, err); }
			);
	}

	toggleDate() {
		this.useDate = !this.useDate;
		this.getData();
	}

	togglePause(stageId: number) {
		this.stageService.togglePause(stageId)
			.subscribe(
				() => { this.toastService.success("Stage updated"); this.getData(); },
				(err) => { console.log(err); ToastApiError(this.toastService, err); }
			);
	}

	viewToday() {
		this.opsPlanningDate = new Date();
		this.getData();
	}
}
